import React, { useRef, useState } from "react";
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  Box, Typography, Button, IconButton, CircularProgress
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { useSelector, useDispatch } from "react-redux";
import {
  uploadAudioForQuestions,
  clearAudioFromQuestions
} from "../../../../redux/Actions/einstufungActions";
import MessageCard from "../../../MessageCard/MessageCard";

const HoerenTab = () => {
  const dispatch = useDispatch();
  const { audioQuestions } = useSelector((state) => state.einstufung);
  const fileInputRef = useRef();

  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [audioRef] = useState(() => React.createRef());
  const [playing, setPlaying] = useState(false);

  const hasAudio = audioQuestions.some(q => q.audioUrl);
  const sharedAudioUrl = hasAudio
    ? process.env.REACT_APP_API_URL + audioQuestions.find(q => q.audioUrl)?.audioUrl
    : null;

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const questionIds = audioQuestions.map((q) => q.id);
    setUploading(true);
    try {
      await dispatch(uploadAudioForQuestions(file, questionIds));
      setMessage({ type: "success", message: "Audio erfolgreich hochgeladen." });
    } catch (error) {
      console.error("Upload failed", error);
      setMessage({ type: "error", message: "Audio-Upload fehlgeschlagen." });
    } finally {
      setUploading(false);
    }
  };

  const handleClear = async () => {
    const questionIds = audioQuestions.map((q) => q.id);
    try {
      await dispatch(clearAudioFromQuestions(questionIds));
      setMessage({ type: "success", message: "Audio erfolgreich gelöscht." });
    } catch (error) {
      setMessage({ type: "error", message: "Audio-Löschung fehlgeschlagen." });
    }
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
      audio.onended = () => setPlaying(false);
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h6">Hören – Audiofragen</Typography>

      {message && (
        <MessageCard
          message={message.message}
          type={message.type}
          onClose={() => setMessage(null)}
        />
      )}

      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <input
          type="file"
          accept="audio/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleUpload}
        />
        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={18} /> : <UploadIcon />}
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          {uploading ? "Hochladen..." : "Audio hochladen"}
        </Button>

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleClear}
          disabled={!hasAudio || uploading}
        >
          Audio entfernen
        </Button>

        <Button
          variant="outlined"
          startIcon={playing ? <PauseIcon /> : <PlayArrowIcon />}
          onClick={handlePlayPause}
          disabled={!hasAudio}
        >
          {playing ? "Pause" : "Abspielen"}
        </Button>

        {sharedAudioUrl && (
          <audio ref={audioRef} src={sharedAudioUrl} hidden preload="auto" />
        )}
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Frage</TableCell>
            <TableCell>Audio</TableCell>
            <TableCell>Optionen</TableCell>
            <TableCell>Korrekte Antwort</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {audioQuestions.map((q) => (
            <TableRow key={q.id}>
              <TableCell>{q.questionText}</TableCell>
              <TableCell>
                <IconButton disabled>
                  {q.audioUrl ? (
                    <VolumeUpIcon color="success" />
                  ) : (
                    <VolumeOffIcon color="disabled" />
                  )}
                </IconButton>
              </TableCell>
              <TableCell>
                {q.options?.map((opt, idx) => (
                  <div key={idx}>{opt}</div>
                ))}
              </TableCell>
              <TableCell>{q.correctAnswer}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default HoerenTab;
